import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';

import { apiSahBudgets } from '~constants/api';

import { rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';
import { download } from '~libs/genericAction';

import type { BreakdownKey, ListSide } from '~support-at-home/components/dashboard/dashboardFigures';

import {
  DashboardBudgetRowsWidget,
  DashboardCareManagementWidget,
  DashboardCaseManagerFilter,
  DashboardCmWorkersWidget,
  DashboardSummaryWidget,
} from '~support-at-home/models/dashboard';

export type DashboardBudgetsReq = {
  quarter: number;
  year: number;
  caseManager: DashboardCaseManagerFilter;
};

/** The api's widget routes: GET /dashboard/{name} */
export type DashboardWidgetName = 'summary' | 'case-management' | 'budgets' | 'cm-workers';

const dashboardParams = (params: DashboardBudgetsReq) => ({
  quarter: `Q${params.quarter}`,
  year: params.year,
  case_manager: params.caseManager === 'all' ? undefined : String(params.caseManager),
});

/** A Show all table: `${list}-${side}`, or the workers (the ids the export route allows). */
export type DashboardExportTable = `${BreakdownKey}-${ListSide}` | 'cm-workers';

/** One Show all table as xlsx, every row (TAV-3482): the appointments export's download, with no filter step. */
export const exportDashboardTable = async ({ table, ...args }: DashboardBudgetsReq & { table: DashboardExportTable }) =>
  download(`${apiSahBudgets}/dashboard/export/${table}`, dashboardParams(args));

// `unwrap` keeps only `data`; the api sends computed_at beside it (TAV-3351), so it is kept on the stored figures
const withComputedAt = <Response>(payload: unknown): Response => ({
  ...unwrap<Response>(payload),
  computed_at: (payload as { computed_at?: string } | undefined)?.computed_at,
});

export const dashboardBudgetsApi = createApi({
  reducerPath: 'dashboardBudgetsApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiSahBudgets }),
  tagTypes: ['dashboard-budgets'],
  endpoints: (builder) => {
    // One endpoint per widget of the Budgets tab: its route and its own tag id, so each can be refetched
    // or invalidated alone
    const widget = <Response>(path: DashboardWidgetName, id: string) =>
      builder.query<Response, DashboardBudgetsReq>({
        query: (params) => ({ url: `/dashboard/${path}`, method: 'GET', params: dashboardParams(params) }),
        providesTags: [{ type: 'dashboard-budgets', id }],
        transformResponse: (payload) => withComputedAt<Response>(payload),
      });

    return {
      // The request behind Refresh (TAV-3351). A mutation, so it leaves no query entry of its own: refreshDashboard
      // writes the figures into the entries the page already reads
      refreshDashboardWidget: builder.mutation<unknown, { widget: DashboardWidgetName; args: DashboardBudgetsReq }>({
        query: ({ widget, args }) => ({
          url: `/dashboard/${widget}`,
          method: 'GET',
          params: { ...dashboardParams(args), refresh: true },
        }),
        transformResponse: (payload) => withComputedAt(payload),
      }),
      getDashboardSummary: widget<DashboardSummaryWidget>('summary', 'SUMMARY'),
      getDashboardCareManagement: widget<DashboardCareManagementWidget>('case-management', 'CASE_MANAGEMENT'),
      getDashboardBudgetRows: widget<DashboardBudgetRowsWidget>('budgets', 'BUDGETS'),
      getDashboardCmWorkers: widget<DashboardCmWorkersWidget>('cm-workers', 'CM_WORKERS'),
    };
  },
});

export const {
  useGetDashboardSummaryQuery,
  useGetDashboardCareManagementQuery,
  useGetDashboardBudgetRowsQuery,
  useGetDashboardCmWorkersQuery,
} = dashboardBudgetsApi;

/**
 * Refresh (TAV-3351): the four widgets again with refresh=true, at once. Only when all four are back are they written
 * into the entries for their args, together, so the page updates as one; if any fails, none is written and the page
 * keeps its figures. Each entry is marked fulfilled with the fresh figures, so a card whose last load failed recovers,
 * and an entry that never loaded is created. RTK Query keeps one entry per quarter and filter (refresh is not in the
 * args).
 */
// The state is `any` because upsertQueryData's thunk wants the state of this api alone, which AppDispatch (the whole store,
// whose type includes this file) does not satisfy
export const refreshDashboard =
  (args: DashboardBudgetsReq) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async (dispatch: ThunkDispatch<any, unknown, UnknownAction>) => {
    const refreshed = <Response>(widget: DashboardWidgetName) =>
      dispatch(dashboardBudgetsApi.endpoints.refreshDashboardWidget.initiate({ widget, args }, { track: false }))
        .unwrap()
        .then((data) => data as Response);
    const [summary, careManagement, budgets, workers] = await Promise.all([
      refreshed<DashboardSummaryWidget>('summary'),
      refreshed<DashboardCareManagementWidget>('case-management'),
      refreshed<DashboardBudgetRowsWidget>('budgets'),
      refreshed<DashboardCmWorkersWidget>('cm-workers'),
    ]);
    // upsertQueryData, not updateQueryData (which patches `data` alone, keeping the error, and skips an entry with no
    // data) or upsertQueryEntries (which, in RTK 2.3, leaves an entry that already exists pending). The four are
    // dispatched in one pass and the store batches RTK Query's actions, so the page hears of them together
    await Promise.all([
      dispatch(dashboardBudgetsApi.util.upsertQueryData('getDashboardSummary', args, summary)),
      dispatch(dashboardBudgetsApi.util.upsertQueryData('getDashboardCareManagement', args, careManagement)),
      dispatch(dashboardBudgetsApi.util.upsertQueryData('getDashboardBudgetRows', args, budgets)),
      dispatch(dashboardBudgetsApi.util.upsertQueryData('getDashboardCmWorkers', args, workers)),
    ]);
  };
